import type {
    LomdaColors,
    LomdaMetadata,
    LomdaScene,
    LomdaStyle,
} from "./lomdaTypes";

/** Default lomda-level style — single source of truth for fallback values. */
export const DEFAULT_LOMDA_STYLE: LomdaStyle = {
    background: "linear-gradient(224.22deg, #2B4D7A 5.83%, #142139 105.21%)",
    textColor: "#c4d8fb",
};

/** Resolve the effective text color for a scene, cascading: scene → lomda → default. */
export function getEffectiveTextColor(
    scene: LomdaScene,
    metadata: LomdaMetadata
): string {
    return (
        scene.style?.textColor ||
        metadata.style?.textColor ||
        DEFAULT_LOMDA_STYLE.textColor
    );
}

/** Resolve the effective background for a scene, cascading: scene → lomda → default. */
export function getEffectiveBackground(
    scene: LomdaScene,
    metadata: LomdaMetadata
): string {
    return (
        scene.style?.background ||
        metadata.style?.background ||
        DEFAULT_LOMDA_STYLE.background
    );
}

/** Resolve the lomda-level style, falling back to defaults for any missing field. */
export function getLomdaStyle(metadata: LomdaMetadata): LomdaStyle {
    return {
        background:
            metadata.style?.background || DEFAULT_LOMDA_STYLE.background,
        textColor: metadata.style?.textColor || DEFAULT_LOMDA_STYLE.textColor,
    };
}

/** Build a scene style update, merging defaults → current → overrides. */
export function buildSceneStyleUpdate(
    currentStyle: Partial<LomdaColors> | undefined,
    metadataStyle: LomdaStyle | undefined,
    overrides: Partial<LomdaColors>
): LomdaColors {
    const defaults: LomdaColors = {
        background: metadataStyle?.background ?? DEFAULT_LOMDA_STYLE.background,
        textColor: metadataStyle?.textColor ?? DEFAULT_LOMDA_STYLE.textColor,
    };
    return {
        ...defaults,
        ...currentStyle,
        ...overrides,
    };
}
